/**
 * Client-side reporter for uncaught React errors. When the root
 * error boundary catches an unhandled exception (or
 * `window.onerror` / `unhandledrejection` fires), this module ships
 * a sanitized event to the BFF observability sink so the team sees
 * customer-facing failures without the customer having to email
 * support.
 *
 * Design constraints — read carefully before changing.
 *
 * 1. **Transport is the same-origin BFF endpoint `/api/observability/error`,
 *    NOT direct to AWS.** Putting AWS credentials in a client bundle
 *    recreates the long-lived-AWS-keys problem (M-2 in
 *    `foundations/SECURITY-AUDIT.md`). The BFF Lambda behind the
 *    endpoint owns the IAM role + the CloudWatch log stream; the
 *    SPA only has to POST same-origin JSON. No env var to configure
 *    per-environment — Amplify Hosting's `/api/*` rewrite handles it.
 *
 * 2. **PII redaction happens BEFORE the network call** because the
 *    error message and stack trace can wrap a real value (an email
 *    a user typed, a `client_uuid` from the URL, a JWT, an API key).
 *    The redaction patterns are conservative on purpose: false
 *    positives ([redacted] over a literal that just LOOKS like a
 *    UUID) are cheap; false negatives leak PII.
 *
 * 3. **Never `console.log` the payload, redacted or not.** The
 *    browser console is reachable by anyone with devtools open —
 *    customer support sessions, screen-share recordings, browser
 *    extensions reading console output. The error boundary's
 *    rendered text already carries a short reference id; that's
 *    the only thing the customer needs to see.
 *
 * 4. **Best-effort transport.** We don't await the POST; the error
 *    boundary needs to render now. If the network call fails, we
 *    swallow — the customer already failed once today, surfacing a
 *    second failure (the reporter dying) helps no one.
 */

const ENDPOINT = "/api/observability/error";

const REDACTED = "[redacted]";

/**
 * Conservative regex set for client-side PII redaction. Each pattern
 * intentionally over-matches: a UUID-looking literal in a stack
 * frame is fine to redact because the lookalike is extremely rare
 * and the cost of a false positive is just slightly less detail in
 * the log.
 *
 * Order matters: we run the JWT pattern before the long-token
 * fallback, because JWTs would also match the long-token shape but
 * we want the cleaner pattern to win.
 */
const REDACTION_PATTERNS: ReadonlyArray<{ name: string; pattern: RegExp }> = [
  // JSON Web Token (Cognito access tokens, ID tokens, refresh tokens).
  {
    name: "jwt",
    pattern:
      /\bey[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g,
  },
  // Email address — RFC 5321 local-part is permissive; this matches
  // the shapes ROOK actually deals with.
  {
    name: "email",
    pattern: /\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/g,
  },
  // UUID v4 (and v1/v3/v5 — same shape). Covers `client_uuid`,
  // `user_uuid`, Cognito `sub`, document ids, etc.
  {
    name: "uuid",
    pattern:
      /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
  },
  // Long opaque tokens (32+ chars, no whitespace). Catches API keys,
  // SDK secrets, HMAC signatures, webhook auth tokens. Last so JWTs
  // / UUIDs above match first with a more specific marker.
  { name: "token", pattern: /\b[A-Za-z0-9+/=_-]{32,}\b/g },
];

const MAX_MESSAGE_LEN = 1024;
const MAX_STACK_LEN = 4096;

const truncate = (s: string, max: number): string =>
  s.length > max ? `${s.slice(0, max)}…[truncated]` : s;

/**
 * Apply every redaction pattern in order. Each pattern's matches
 * become the literal string `[redacted]` so log search remains
 * possible (a customer reporting "I saw [redacted] in the error"
 * still maps back to a recognisable shape).
 */
export const redactString = (input: string): string => {
  let out = input;
  for (const { pattern } of REDACTION_PATTERNS) {
    out = out.replace(pattern, REDACTED);
  }
  return out;
};

export type ErrorBoundaryEvent = {
  /** Short reference id shown in the SPA's fallback UI. */
  reference: string;
  /** Constructor name (`TypeError`, `RangeError`, custom). */
  errorName: string;
  /** `error.message` from the boundary; redacted + truncated here. */
  errorMessage: string;
  /** `error.stack` from the boundary; redacted + truncated here. */
  stack?: string;
  /**
   * React component stack from `componentDidCatch`. Smaller than
   * the JS stack but useful to locate the rendering tree above the
   * thrown component. Same redaction rules apply.
   */
  componentStack?: string;
  /** Pathname at the time of the failure (`window.location.pathname`). */
  route: string;
  /**
   * Coarse navigator info. Helps reproduce browser-specific bugs
   * without identifying the individual user.
   */
  userAgent: string;
  /** ISO 8601 timestamp; the boundary stamps it before calling. */
  timestamp: string;
};

/**
 * Public surface for tests + the boundary. Sanitises the event
 * without sending it.
 */
export const sanitizeErrorEvent = (
  event: ErrorBoundaryEvent,
): ErrorBoundaryEvent => ({
  reference: event.reference,
  errorName: event.errorName,
  errorMessage: truncate(redactString(event.errorMessage), MAX_MESSAGE_LEN),
  stack:
    event.stack === undefined
      ? undefined
      : truncate(redactString(event.stack), MAX_STACK_LEN),
  componentStack:
    event.componentStack === undefined
      ? undefined
      : truncate(redactString(event.componentStack), MAX_STACK_LEN),
  route: event.route,
  userAgent: event.userAgent,
  timestamp: event.timestamp,
});

/**
 * Fire-and-forget reporter. Returns immediately; the network call
 * happens in the background with `keepalive: true` so it survives
 * even if the user navigates away during the boundary render.
 *
 * Returns `true` if the request was dispatched, `false` if the
 * function decided not to dispatch (fetch unavailable, e.g. in
 * SSG/SSR contexts or test runners that don't polyfill fetch).
 * Useful for test introspection; production callers can ignore.
 */
export const reportErrorBoundary = (event: ErrorBoundaryEvent): boolean => {
  if (typeof globalThis.fetch !== "function") {
    return false;
  }

  const payload = sanitizeErrorEvent(event);

  // We drop the result on purpose: a failing reporter must not
  // surface to the customer, and we don't have a useful retry
  // strategy for a boundary event (the page is already broken).
  globalThis
    .fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    })
    .catch(() => {
      // Intentional swallow — see file-level point 4.
    });

  return true;
};

/**
 * Counterpart to `reportErrorBoundary` for query / data-fetch failures
 * that the per-view `<ViewErrorRetry>` catches and renders as a generic
 * "couldn't load this" card.
 *
 * Why this exists separately: the view card cannot render the raw
 * `error.message` because the backend sometimes echoes upstream
 * diagnostic strings the customer must not see (caught 2026-05-22
 * after Pepe spotted a hashed Authorization header in a webhooks
 * error card). We render a short reference id, and the actual
 * error — redacted and truncated — ships here so the team can grep
 * CloudWatch by reference.
 *
 * Structurally identical to ErrorBoundaryEvent + a `surface` tag
 * (the title of the failing view: "Data webhook security",
 * "JSON Simulator", etc.) so logs are grep-able by surface.
 */
export type ViewErrorEvent = Omit<ErrorBoundaryEvent, "componentStack"> & {
  /** Short label of the failing surface — the view title. */
  surface: string;
};

export const reportViewError = (event: ViewErrorEvent): boolean => {
  if (typeof globalThis.fetch !== "function") {
    return false;
  }

  const payload = {
    ...sanitizeErrorEvent({ ...event, componentStack: undefined }),
    surface: event.surface,
    kind: "view-error" as const,
  };

  globalThis
    .fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    })
    .catch(() => {
      // Intentional swallow — same posture as reportErrorBoundary.
    });

  return true;
};
